<script lang="ts">
	import type { Mediator, SvelteAsr } from 'types/common'
	import type { BaseAttachmentFile } from '@isoftdata/svelte-attachments'
	import type { SalesOrderCustomerMessage, SalesOrderInventoryMessage } from './sales-order'
	import { graphql, type InitialLoadQuery$result, type SalesOrder$result } from '$houdini'
	import { formatCustomerCurrency } from 'utility/customer-helper'
	import { getEventValue } from '@isoftdata/browser-event'
	import { formatISO } from 'date-fns'
	import { getContext, onMount, tick } from 'svelte'

	import Button from '@isoftdata/svelte-button'
	import Fieldset from '@isoftdata/svelte-fieldset'
	import Input from '@isoftdata/svelte-input'
	import Modal from '@isoftdata/svelte-modal'
	import Select from '@isoftdata/svelte-select'
	import StateCardHeader from 'components/StateCardHeader.svelte'
	import CustomerInfoCard from './CustomerInfoCard.svelte'
	import SalesOrderDetailCard from './SalesOrderDetailCard.svelte'

	// Feedbacks (could work on this in the next version):
	// - maybe, add thumbnail image for each line item, if available
	// - have the line items' individual messages in the line item details tab as well
	// - add badges on the tabs for the line items, adjustments, attachments, messages, and payments to indicate the number of items in each tab, or just icons, whichever makes sense
	// - for messages, we need some indicator to show that there are any important messages

	interface Props {
		asr: SvelteAsr
		salesOrderId: number | null
		storeId: number | null
		salesOrder: SalesOrder$result['salesOrder']
		salesPersonList: InitialLoadQuery$result['userAccounts']
		shippingMethods: InitialLoadQuery$result['shippingMethods']
		taxItems: InitialLoadQuery$result['taxItems']
		attachments: Array<BaseAttachmentFile & { salesOrderFileId: number }>
		SODucumnetTypes: InitialLoadQuery$result['salesOrderDocumentTypes']
		salesOrderTerms: InitialLoadQuery$result['salesOrderTerms']
		salesOrderCustomerMessage: SalesOrderCustomerMessage | null
		salesOrderInventoryMessage: SalesOrderInventoryMessage | null
		SOSettingValues: InitialLoadQuery$result['settingValues']['salesOrders']
	}

	let {
		asr,
		salesOrderId = null,
		storeId = null,
		salesOrder,
		salesPersonList,
		shippingMethods,
		taxItems,
		attachments,
		SODucumnetTypes,
		salesOrderTerms,
		salesOrderCustomerMessage = null,
		salesOrderInventoryMessage = null,
		SOSettingValues = { showSequentialInvoiceNumbers: false },
	}: Props = $props()

	const SOBillSummary: { label: string; value: string | undefined; bold: boolean }[] = $state([
		{ label: 'Subtotal', value: salesOrder?.subtotal, bold: false },
		{ label: 'Tax', value: salesOrder?.tax, bold: false },
		{ label: 'Total', value: salesOrder?.total, bold: true },
		{ label: 'Paid', value: salesOrder?.paid, bold: false },
		{ label: 'Balance', value: salesOrder?.balance, bold: false },
	])

	const mediator = getContext<Mediator>('mediator')
	let showModal = $state(false)
	let isValidatingSalesOrder = $state(false)
	let modalErrorMessage: string | null = $state(null)

	async function openSOLookupModal() {
		showModal = true
		await tick()
		document.getElementById('sales-order-number-input')?.focus()
	}

	async function validateSalesOrder() {
		if (!salesOrderId || !storeId) {
			modalErrorMessage = 'Please enter a valid Sales Order Number and Store ID.'
			return
		}

		isValidatingSalesOrder = true
		try {
			const { data } = await validateSalesOrderQuery.fetch({
				variables: {
					key: {
						storeId,
						salesOrderId,
					},
				},
			})

			if (!data?.salesOrder) {
				throw new Error('Sales Order not found.')
			}
			showModal = false
			modalErrorMessage = null
			// Reload the state to get the sales order data
			asr.go(null, { salesOrderId, storeId }, { inherit: false })
		} catch (error) {
			modalErrorMessage = `Sales Order ${salesOrderId} not found in Store ${storeId}. Please check the Sales Order Number and Store ID.`
		} finally {
			isValidatingSalesOrder = false
		}
	}

	const validateSalesOrderQuery = graphql(`
		query ValidateSalesOrder($key: SalesOrderKey!) {
			salesOrder(key: $key) {
				id
			}
		}
	`)

	onMount(() => {
		if (salesOrder && salesOrderId && storeId) {
			const activityTitle = `#${salesOrder.id} (${salesOrder.documentType.name})`
			mediator.publish('activity', {
				icon: 'dollar',
				type: 'SALES_ORDER',
				route: 'app.sales-order',
				name: activityTitle,
				title: activityTitle,
				parameters: {
					storeId: storeId.toString(),
					salesOrderId: salesOrderId.toString(),
				},
			})
		}
	})
</script>

<div class="card">
	<StateCardHeader
		icon="dollar-sign"
		title="Sales Order"
	>
		<svelte:fragment slot="right">
			<div class="d-flex">
				<Button
					disabled
					outline
					size="sm"
					class="ml-2"
					color="success"
					iconClass="plus"
				>
					New Sale
				</Button>
				<Button
					disabled
					outline
					class="ml-2"
					size="sm"
					color="primary"
					iconClass="save"
				>
					Save Sales Order
				</Button>
			</div>
		</svelte:fragment>
	</StateCardHeader>
	<div class="card-body">
		<div class="row">
			<div class="col-12 col-lg-2">
				<Fieldset
					label="Document"
					class="h-100"
				>
					<div class="row">
						<div class="col-12">
							<Input
								readonly
								label="Order #"
								labelParentClass="mb-0"
								placeholder="New Order"
								class="cursor-pointer"
								value={storeId && salesOrderId ? `${storeId}-${salesOrderId}` : ''}
								onclick={async () => {
									await openSOLookupModal()
								}}
							>
								{#snippet append()}
									<Button
										size="sm"
										iconClass="search"
										onclick={async () => {
											await openSOLookupModal()
										}}
									/>
								{/snippet}
							</Input>
						</div>
						<div class="col-12">
							<Select
								disabled
								label="Document Type"
								value={salesOrder?.documentType.id || null}
							>
								{#each SODucumnetTypes as type}
									<option value={type.id}>{type.name}</option>
								{/each}
							</Select>
						</div>
						{#if SOSettingValues.showSequentialInvoiceNumbers}
							<div class="col-12">
								<Input
									readonly
									label="Invoice #"
									value={salesOrder?.sequentialId?.toString() || ''}
								/>
							</div>
						{/if}
						<div class="col-12">
							<Input
								readonly
								type="date"
								label="Date"
								value={salesOrder?.date ? formatISO(salesOrder.date, { representation: 'date' }) : ''}
							/>
						</div>
					</div>
				</Fieldset>
			</div>
			<div class="col-12 col-md-10">
				<CustomerInfoCard
					{asr}
					{salesOrder}
					{salesPersonList}
					{shippingMethods}
					{taxItems}
					selectedCustomerDetailTab="CUSTOMER_DETAIL"
				/>
			</div>
		</div>
		<hr />
		<SalesOrderDetailCard
			{asr}
			{salesOrder}
			{attachments}
			{salesOrderTerms}
			{salesOrderCustomerMessage}
			{salesOrderInventoryMessage}
		/>
		<hr class="mb-1" />
		<div class="row">
			{#each SOBillSummary as item}
				<div class="col-6 col-md-1">
					<Input
						readonly
						label={item.label}
						class="text-right {item.bold ? 'font-weight-bold' : ''}"
						value={item.value ? formatCustomerCurrency(item.value) : '$0.00'}
					/>
				</div>
			{/each}
		</div>
	</div>
</div>

<Modal
	show={showModal}
	confirmButtonType="submit"
	confirmButtonFormId="sales-order-validation-form"
	confirmButtonText="Load Sale"
	confirmButtonColor="primary"
	confirmButtonDisabled={!storeId || !salesOrderId}
	confirmButtonIsLoading={isValidatingSalesOrder}
	cancelButtonText="Cancel"
	close={() => (showModal = false)}
	size="sm"
	title="Load Sales Order"
>
	<form
		id="sales-order-validation-form"
		onsubmit={event => {
			event.preventDefault()
			validateSalesOrder()
		}}
	>
		<div class="row">
			<div class="col-4">
				<Input
					label="Store"
					labelParentClass="mb-0"
					bind:value={storeId}
				/>
			</div>
			<div class="col-8">
				<Input
					id="sales-order-number-input"
					label="Sales Order Number"
					labelParentClass="mb-0"
					value={null}
					onchange={event => {
						salesOrderId = parseInt(getEventValue(event), 10)
					}}
				/>
			</div>
		</div>
	</form>
	{#if modalErrorMessage}
		<div class="alert alert-danger mt-2">
			{modalErrorMessage}
		</div>
	{/if}
</Modal>
